import { useState, useCallback, useMemo, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, Download, Plus } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { ListPage } from '@/shared/components/crud/list-page'
import { useServerTable } from '@/shared/hooks/use-server-table'
import { usePersistedTableState } from '@/shared/hooks/use-persisted-table-state'
import { usePermissions } from '@/shared/lib/permissions/use-permissions'
import { Permissions, PermissionLevel } from '@/shared/lib/permissions/permission-values'
import {
  fetchAccountList, fetchAccountFilterValues, exportAccounts,
  buildAccountFilter, reconnectOpenBanking,
  type AccountListResponse,
} from '../api/bank-accounts-api'
import { useBankAccountColumns } from './bank-account-columns'
import { AddAccountWizard } from './add-account-wizard'
import { SetBalanceDialog } from './set-balance-dialog'
import { AccountDeleteConfirmation } from './account-delete-confirmation'
import { BankStatementUploadDialog } from '@/features/bank-statements/components/bank-statement-upload-dialog'
import type { BankAccount } from '../types/bank-account'

type Tab = 'all' | 'exceptions' | 'disconnected'

const EXPORT_FIELDS = [
  'accountName', 'currencyIsoCode', 'institutionName', 'companyName',
  'iban', 'accountNumber', 'closingBalance', 'closingBalanceDate',
  'availableBalance', 'connectivity', 'connectionStatus', 'active', 'lastBankUpdate',
]

const DEFAULT_SORTING = [{ id: 'accountName', desc: false }]
const DEFAULT_TAB: Tab = 'all'

function readTabFromExtras(extras: Record<string, unknown> | undefined): Tab {
  const raw = extras?.tab
  if (raw === 'exceptions' || raw === 'disconnected' || raw === 'all') return raw
  return DEFAULT_TAB
}

export function BankAccountsPage() {
  const persisted = usePersistedTableState({
    pageKey: 'bank-accounts',
    legacyStorageKey: 'bank-accounts',
  })

  const [tab, setTab] = useState<Tab>(() => readTabFromExtras(persisted.state?.extras))
  const [wizardOpen, setWizardOpen] = useState(false)
  const { can } = usePermissions()
  const canCreate = can({ name: Permissions.BankAccounts, level: PermissionLevel.CreateEdit })
  const [editAccount, setEditAccount] = useState<BankAccount | null>(null)
  const [deleteAccount, setDeleteAccount] = useState<BankAccount | null>(null)
  const [balanceAccount, setBalanceAccount] = useState<BankAccount | null>(null)
  const [uploadAccount, setUploadAccount] = useState<BankAccount | null>(null)

  const canHydrate = persisted.isReady
  const tableState = useServerTable({
    defaultPageSize: 50,
    defaultSorting: DEFAULT_SORTING,
    initialFilters: canHydrate ? persisted.state?.columnFilters ?? [] : undefined,
    initialSorting: canHydrate ? persisted.state?.sorting ?? DEFAULT_SORTING : undefined,
    onStateChange: ({ columnFilters, sorting }) => {
      persisted.saveColumnFilters(columnFilters)
      persisted.saveSorting(sorting)
    },
  })

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  const { data: filterValues } = useQuery({
    queryKey: ['Accounts', 'filterValues'],
    queryFn: fetchAccountFilterValues,
    staleTime: 10 * 60 * 1000,
  })

  const { data, isFetching } = useQuery<AccountListResponse>({
    queryKey: ['Accounts', 'list', tableState.columnFilters, tableState.sorting, tableState.pagination, tab],
    queryFn: () => {
      const tabState = { exceptions: tab === 'exceptions', disconnected: tab === 'disconnected' }
      return fetchAccountList({
        filter: buildAccountFilter(tableState.columnFilters, filterValues, tabState),
        sorting: tableState.request.Sorting,
        pagination: tableState.request.Pagination,
      })
    },
    placeholderData: (prev) => prev,
  })

  const exportMutation = useMutation({
    mutationFn: (ext: 'csv' | 'xlsx') => exportAccounts(ext, EXPORT_FIELDS),
    onSuccess: (blob, ext) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `bank-accounts.${ext}`
      a.click()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error('Export failed'),
  })

  const reconnectMutation = useMutation({
    mutationFn: (accountId: number) => reconnectOpenBanking(accountId),
    onSuccess: (data) => {
      const url = data.redirectUrl ?? data.url ?? data.Link
      if (url) {
        window.location.href = url
      } else {
        toast.error('No redirect URL returned from bank')
      }
    },
    onError: () => toast.error('Failed to initiate reconnection'),
  })

  const handleTabChange = useCallback((value: string) => {
    const nextTab = value as Tab
    setTab(nextTab)
    tableState.onPaginationChange({ pageIndex: 0, pageSize: tableState.pagination.pageSize })
    persisted.saveExtras({ tab: nextTab })
  }, [tableState, persisted])

  const columns = useBankAccountColumns({
    filterValues,
    onEdit: setEditAccount,
    onDelete: setDeleteAccount,
    onUpdateBalance: setBalanceAccount,
    onUploadStatement: setUploadAccount,
    onReconnect: (account) => {
      reconnectMutation.mutate(account.id)
    },
  })

  const TOOLBAR_BTN = 'h-7 gap-1.5 px-2 text-toolbar-foreground/70 hover:text-toolbar-foreground hover:bg-toolbar-foreground/5'

  const headerActions = useMemo(() => (
    <>
      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="h-7 bg-white/10 border-0 p-0.5 gap-0">
          <TabsTrigger value="all" className="text-xs px-2.5 h-6 rounded-sm text-toolbar-foreground/60 data-[state=active]:bg-white/20 data-[state=active]:text-toolbar-foreground data-[state=active]:shadow-none">All</TabsTrigger>
          <TabsTrigger value="exceptions" className="text-xs px-2.5 h-6 rounded-sm text-toolbar-foreground/60 data-[state=active]:bg-white/20 data-[state=active]:text-toolbar-foreground data-[state=active]:shadow-none">Exceptions</TabsTrigger>
          <TabsTrigger value="disconnected" className="text-xs px-2.5 h-6 rounded-sm text-toolbar-foreground/60 data-[state=active]:bg-white/20 data-[state=active]:text-toolbar-foreground data-[state=active]:shadow-none">Disconnected</TabsTrigger>
        </TabsList>
      </Tabs>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={TOOLBAR_BTN}>
            <Download className="h-3.5 w-3.5" />
            <span className="hidden lg:inline text-xs">Export</span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => exportMutation.mutate('csv')}>CSV</DropdownMenuItem>
          <DropdownMenuItem onClick={() => exportMutation.mutate('xlsx')}>Excel</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {canCreate && (
        <Button variant="ghost" size="sm" className={TOOLBAR_BTN} onClick={() => setWizardOpen(true)}>
          <Plus className="h-3.5 w-3.5" />
          <span className="hidden lg:inline text-xs">Add Account</span>
        </Button>
      )}
    </>
  ), [tab, handleTabChange, canCreate, exportMutation])

  const description = data
    ? `${data.TotalActives} active account${data.TotalActives !== 1 ? 's' : ''}`
    : 'Manage your connected and manual bank accounts'

  const handleReset = () => {
    persisted.reset()
    tableState.onColumnFiltersChange([])
    tableState.onSortingChange(DEFAULT_SORTING)
    setTab(DEFAULT_TAB)
  }

  return (
    <>
      <ListPage
        title="Bank Accounts"
        description={description}
        columns={columns}
        data={data?.Records ?? []}
        totalRows={data?.TotalActives ?? 0}
        isLoading={isFetching}
        tableState={tableState}
        permissionName={Permissions.BankAccounts}
        headerActions={headerActions}
        columnVisibility={persisted.state?.columnVisibility}
        onColumnVisibilityChange={persisted.saveColumnVisibility}
        hasSavedView={persisted.hasSavedView}
        onResetView={handleReset}
      />

      <AddAccountWizard open={wizardOpen} onOpenChange={setWizardOpen} />
      <AddAccountWizard
        open={editAccount !== null}
        onOpenChange={(open) => !open && setEditAccount(null)}
        editingAccount={editAccount}
      />
      <SetBalanceDialog account={balanceAccount} onOpenChange={(open) => !open && setBalanceAccount(null)} />
      <BankStatementUploadDialog
        open={uploadAccount !== null}
        onOpenChange={(open) => !open && setUploadAccount(null)}
        accountId={uploadAccount?.id}
      />

      <AccountDeleteConfirmation
        account={deleteAccount}
        onOpenChange={(open) => !open && setDeleteAccount(null)}
      />
    </>
  )
}
